import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Download, FileText, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/checkout/status/${sessionId}`
        );
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentData(data);
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts >= maxAttempts - 1) {
          setStatus("error");
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  if (status === "loading") {
    return (
      <div className="pt-24 pb-12 px-6 lg:px-8 min-h-screen flex items-center justify-center" data-testid="checkout-loading">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
          <h2 className="font-heading text-2xl font-bold mb-2">
            Processing Payment...
          </h2>
          <p className="text-muted-foreground">
            Please wait while we confirm your purchase
          </p>
        </motion.div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="pt-24 pb-12 px-6 lg:px-8 min-h-screen flex items-center justify-center" data-testid="checkout-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg"
        >
          <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="font-heading text-4xl font-black mb-4">
            Payment Successful!
          </h1>
          <p className="text-muted-foreground text-lg mb-2">
            Thank you for your purchase!
          </p>
          {paymentData?.metadata?.beat_title && (
            <p className="text-xl font-heading font-bold text-primary mb-2">
              "{paymentData.metadata.beat_title}"
            </p>
          )}
          {paymentData?.metadata?.license_name && (
            <p className="text-muted-foreground mb-8">
              License: {paymentData.metadata.license_name}
            </p>
          )}

          <div className="glass rounded-2xl p-6 mb-8 text-left">
            <h3 className="font-heading font-bold mb-2">Your Downloads</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Your beat and license agreement are ready. Save these files now — you can also
              bookmark this page to return to them.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`${BACKEND_URL}/api/download/beat/${sessionId}`}
                className="flex-1"
                data-testid="download-beat-link"
              >
                <Button className="w-full rounded-xl bg-primary hover:bg-primary/90 neon-glow gap-2">
                  <Download className="w-4 h-4" />
                  Download Beat
                </Button>
              </a>
              <a
                href={`${BACKEND_URL}/api/download/license/${sessionId}`}
                className="flex-1"
                data-testid="download-license-link"
              >
                <Button variant="outline" className="w-full rounded-xl gap-2">
                  <FileText className="w-4 h-4" />
                  License Agreement (PDF)
                </Button>
              </a>
            </div>
          </div>

          <div className="flex justify-center">
            <Link to="/store">
              <Button variant="ghost" className="rounded-full px-8 gap-2" data-testid="browse-more-btn">
                Browse More Beats
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error/Expired/Timeout states
  return (
    <div className="pt-24 pb-12 px-6 lg:px-8 min-h-screen flex items-center justify-center" data-testid="checkout-error">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        <div className="w-24 h-24 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-8">
          <XCircle className="w-12 h-12 text-destructive" />
        </div>
        <h1 className="font-heading text-3xl font-bold mb-4">
          {status === "expired" ? "Session Expired" : "Something Went Wrong"}
        </h1>
        <p className="text-muted-foreground mb-8">
          {status === "expired"
            ? "Your checkout session has expired. Please try again."
            : "We couldn't verify your payment. If you were charged, please contact us."}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/store">
            <Button variant="outline" className="rounded-full px-8">
              Back to Store
            </Button>
          </Link>
          <Link to="/contact">
            <Button className="rounded-full px-8 bg-primary hover:bg-primary/90">
              Contact Support
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
